import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import FontKeys from '../FontKeys';
import AnalogClock from '../game/AnalogClock';
import DigitalClock from '../game/DigitalClock';
import ManRocket from '../game/ManRocket';
import StateButton from '../game/StateButton';
import gameData, { DEFAULT_BUTTON_CONFIG } from '../gameData';
import SoundAssetKeys from '../SoundAssetKeys';
import { playJingle, stopJingle } from '../SoundManager';
import UIAssetKeys from '../UIAssetKeys';
import EndScreen from './EndScreen';
import SelectLevelScreen from './SelectLevelScreen';
import ABCyaLib from '@abcya/abcya_lib';
import ClockScreen from './ClockScreen';
import IntroScreen from './IntroScreen';
import HelpPage from '../game/HelpPage';

export default class GameScreen extends Phaser.Scene {
  static get KEY() { return 'GameScreen'; }

  constructor() {
    super({ key: GameScreen.KEY });
  }

  init() {
    /** @type {Phaser.GameObjects.Image[]} */
    this.bars = [];
    this.answersCorrect = 0;
    this.timeQuestion = null;
    this.timeQuestionBg = null;
    this.wrong = null;

    if (gameData.CURRENT_LEVEL === 0) gameData.CURRENT_LEVEL = 1;
  }

  create() {
    // each level has its own music so stop and start
    stopJingle();

    if (gameData.MUSIC_ON === true) {
      playJingle(`theme_level${gameData.CURRENT_LEVEL}`);
    }

    this.backgroundImage = ABCyaPhaser.Images.addImage(0,0,`background_level${gameData.CURRENT_LEVEL}`, this).setOrigin(0);

    if (gameData.CLOCK_IS_ANALOG === true) {
      this.clock = new AnalogClock(this);
    }
    else {
      this.clock = new DigitalClock(this);
    }
    this.add.existing(this.clock);
    this.clock.x = this.scale.width / 2;
    this.clock.y = this.scale.height / 2 - 50;
    if (gameData.CURRENT_LEVEL === 4 || (ABCyaLib.Environment.instance.gameConfig.levelsOverride && ABCyaLib.Environment.instance.gameConfig.levelsOverride[gameData.CURRENT_LEVEL - 1].level === 4)) {
      // clock needs to be higher to fit more text
      this.clock.y = this.scale.height / 2 - 100;
    }

    this.clock.on('CHANGED', this.changeBackgroundForTime, this);

    this.progress = ABCyaPhaser.Images.addImage(this.scale.width / 2, this.scale.height - 60, UIAssetKeys._progress_background, this);

    this.menuButton = ABCyaPhaser.Buttons.addDefaultButton(this.scale.width - 70, 70, UIAssetKeys._menu_button, null, SoundAssetKeys._click, this.cleanUpLevel.bind(this), this, { ...DEFAULT_BUTTON_CONFIG });

    this.checkButton = this.add.container(90, this.scale.height - 95);
    const checkBtnBg = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._check_button, this.checkButton);
    ABCyaPhaser.Container.setContainerInteractive(this.checkButton);
    const checkBtnTxt = ABCyaPhaser.Text.addText(0,0,48, 0xfffdfd, FontKeys._dimboregular, ABCyaPhaser.Localization.instance.getLocalizedString('game_screen.check_button'), this.checkButton, true).setShadow(2,2, '#cd1b06')
      .setPadding(2);
    ABCyaPhaser.Text.resizeFontToFitWidth(checkBtnTxt, 108);
    ABCyaPhaser.Buttons.convertToButton(this.checkButton, { onClick: this.checkAnswer.bind(this), ...DEFAULT_BUTTON_CONFIG });

    this.infoButton = ABCyaPhaser.Buttons.addDefaultButton(this.scale.width - 170, this.scale.height - 60, UIAssetKeys._info_button, null, SoundAssetKeys._click, this.showHelpPage.bind(this), this, { ...DEFAULT_BUTTON_CONFIG });

    // make toggle button for music
    this.musicButton = new StateButton(this, UIAssetKeys._speaker_on, UIAssetKeys._speaker_off);
    this.musicButton.x = this.scale.width - 70;
    this.musicButton.y = this.scale.height - 60;
    this.add.existing(this.musicButton);
    this.musicButton.doAction1 = this.speakerOff.bind(this);
    this.musicButton.doAction2 = this.speakerOn.bind(this);

    if (gameData.MUSIC_ON === false) {
      this.musicButton.switchStateOff();
    }

    this.cameras.main.zoom = 0;
    this.transitionIn();

    this.hideAll();
  }

  changeBackgroundForTime () {
    const hourMinuteArray = this.clock.getHoursAndMinutes();

    const clockHour = hourMinuteArray[0];
    const clockMinute = hourMinuteArray[1];
    const timeOfDay = hourMinuteArray[2];

    const minutesIn12Hours = 12 * 60;

    // 12am (midnight) should be darkest
    // 12pm (noon) should be brightest
    const minutesPast12 = (clockHour === 12 ? 0 : clockHour) * 60 + clockMinute;

    // get the percent of the way through this 12 hour period
    let percent = minutesPast12 / minutesIn12Hours;

    if (percent < (4 * 60 / minutesIn12Hours)) {
      // if before 4am or 4pm, ease from 0 to 0.2
      const percentThroughStage = Phaser.Math.Percent(minutesPast12, 0,4 * 60);
      percent = Phaser.Math.FromPercent(percentThroughStage, 0, 0.2);
    }
    else if (percent > (8 * 60 / minutesIn12Hours)) {
      // if after 8am or 8pm, ease from 0.8 to 1
      const percentThroughStage = Phaser.Math.Percent(minutesPast12, 8 * 60,minutesIn12Hours);
      percent = Phaser.Math.FromPercent(percentThroughStage, 0.8, 1);
    }
    else {
      // we're in the 4am/4pm to 8am/8pm range... use an ease to go from 0.2 to 0.8
      const percentThroughStage = Phaser.Math.Percent(minutesPast12, 4 * 60, 8 * 60);

      // use a different ease to help offset the values
      const easePercentThroughStage = timeOfDay === 'AM' ? Phaser.Math.Easing.Circular.Out(percentThroughStage) : Phaser.Math.Easing.Sine.Out(percentThroughStage);
      percent = Phaser.Math.FromPercent(easePercentThroughStage, 0.2, 0.8);
    }

    if (timeOfDay === 'PM') {
      // percent needs to be the reciprocal 
      percent = 1 - percent;
    }

    const value = 0.8 * percent;
    this.backgroundImage.alpha = 0.2 + value;
  }

  speakerOff () {
    gameData.MUSIC_ON = false;
    stopJingle();
  }

  speakerOn () {
    gameData.MUSIC_ON = true;
    playJingle(`theme_level${gameData.CURRENT_LEVEL}`);
  }

  transitionIn () {
    const totalRotation = 2 * 360;
    this.tweens.add({ targets: { value: 0 },
      duration: 700,
      ease: Phaser.Math.Easing.Linear,
      props: { value: 1 },
      onUpdate: (tween, target) => {
        this.cameras.main.setAngle(target.value * totalRotation);
      } });
    ABCyaPhaser.Tweens.addTween(this.cameras.main,700, { zoom: 1 }, Phaser.Math.Easing.Linear, () => this.rocketLand());
  }

  rocketLand () {
    this.rocket = new ManRocket(this);
    this.add.existing(this.rocket);

    this.children.moveTo(this.rocket, 3);

    this.rocket.x = this.scale.width / 2 - 300;
    this.rocket.y = this.scale.height / 2 - 1200;

    this.rocket.land();

    this.time.delayedCall(6000, this.showAll, null, this);
    this.time.delayedCall(6500, this.makeQuestion,null, this);
  }

  showAll () {
    ABCyaPhaser.Tweens.addTween([this.clock, this.progress, this.musicButton, this.infoButton, this.checkButton, this.menuButton], 700, { alpha: 1 }, Phaser.Math.Easing.Linear);
  }

  makeQuestion () {
    if (ABCyaLib.Environment.instance.gameConfig.levelsOverride !== null) {
      // custom question generation
      const levelData = ABCyaLib.Environment.instance.gameConfig.levelsOverride[gameData.CURRENT_LEVEL - 1];
      this.makeCustomTimeQuestion(levelData);
    }
    else {
      switch (gameData.CURRENT_LEVEL) {

        case 0:
        case 1:
          this.makeTimeQuestionLevel1();
          break;

        case 2:
          this.makeTimeQuestionLevel2();
          break;

        case 3:
          this.makeTimeQuestionLevel3();
          break;

        case 4:
          this.makeTimeQuestionLevel4();
          break;
      }
    }
  }

  makeCustomTimeQuestion (levelData) {
    if (this.timeQuestion) {
      const currentTimeQuestion = this.timeQuestion;
      const finalPosition = currentTimeQuestion.x + 1000;
      ABCyaPhaser.Tweens.addTween(currentTimeQuestion, 300, { x: currentTimeQuestion.x - 150 }, Phaser.Math.Easing.Linear, () => {
        ABCyaPhaser.Tweens.addTween(currentTimeQuestion, 600, { x: finalPosition }, Phaser.Math.Easing.Linear, () => {
          currentTimeQuestion.destroy();
        });
      });
    }

    if (levelData.level) {
      // use the level
      switch (levelData.level) {

        case 0:
        case 1:
          this.makeTimeQuestionLevel1();
          break;

        case 2:
          this.makeTimeQuestionLevel2();
          break;

        case 3:
          this.makeTimeQuestionLevel3();
          break;

        case 4:
          this.makeTimeQuestionLevel4();
          break;
      }
      return;
    }

    this.questionTime = 'AM';
    this.questionHour = Phaser.Math.Between(1, 12);
    this.questionMinutes = 0;

    if (levelData.allowedMinutes) {
      if (levelData.interval) {
        // minutes can either be a specific minute or an interval, randomly pick one
        if (Phaser.Math.Between(0, 1) === 0) {
          // pick interval
          let sections = Math.floor(60 / levelData.interval);
          if (60 % levelData.interval === 0) sections -= 1;
          this.questionMinutes = Phaser.Math.Between(0,sections) * levelData.interval;
        }
        else this.questionMinutes = Phaser.Utils.Array.GetRandom(levelData.allowedMinutes);
      }
      else this.questionMinutes = Phaser.Utils.Array.GetRandom(levelData.allowedMinutes);
    }
    else if (levelData.interval) {
      let sections = Math.floor(60 / levelData.interval);
      if (60 % levelData.interval === 0) sections -= 1;
      this.questionMinutes = Phaser.Math.Between(0,sections) * levelData.interval;
    }
    else this.questionMinutes = Phaser.Math.Between(0, 59);

    const timeChoice = Phaser.Math.Between(0,1);
    if (timeChoice === 1) {
      this.questionTime = 'PM';
    }

    const minutesString = this.questionMinutes < 10 ? `0${this.questionMinutes}` : this.questionMinutes;

    const timeString = this.questionMinutes > 0 ? `${this.questionHour}:${minutesString} ${this.questionTime}` : `${this.questionHour} ${this.questionTime}`;

    this.timeQuestion = ABCyaPhaser.Text.addText(this.scale.width / 2 - 1000, this.scale.height - 170, '34pt', 0xffffff, FontKeys._dimboregular,`Set the clock to ${timeString}`,this, true).setOrigin(0.5,0);

    let delay = 0;
    if (this.answersCorrect > 0) {
      delay = 500;
    }
    ABCyaPhaser.Tweens.addTween(this.timeQuestion, 1300, { delay, x: this.timeQuestion.x + 1000 }, Phaser.Math.Easing.Bounce.Out);
  }

  makeTimeQuestionLevel1 () {
    if (this.timeQuestion) {
      const currentTimeQuestion = this.timeQuestion;
      const finalPosition = currentTimeQuestion.x + 1000;
      ABCyaPhaser.Tweens.addTween(currentTimeQuestion, 300, { x: currentTimeQuestion.x - 150 }, Phaser.Math.Easing.Linear, () => {
        ABCyaPhaser.Tweens.addTween(currentTimeQuestion, 600, { x: finalPosition }, Phaser.Math.Easing.Linear, () => {
          currentTimeQuestion.destroy();
        });
      });
    }

    this.questionTime = 'AM';
    this.questionHour = Phaser.Math.Between(1, 12);
    this.questionMinutes = 0;
    switch (gameData.CURRENT_LEVEL) {
      case 2:
        this.questionMinutes = Phaser.Math.Between(1, 11) * 5;
        break;
    }
    const timeChoice = Phaser.Math.Between(0,1);
    if (timeChoice === 1) {
      this.questionTime = 'PM';
    }

    const minutesString = this.questionMinutes < 10 ? `0${this.questionMinutes}` : this.questionMinutes;

    const timeString = `${this.questionMinutes > 0 ? `${this.questionHour}:${minutesString}` : `${this.questionHour}`} ${this.getLocalizedTimeLabel()}`;

    this.timeQuestion = ABCyaPhaser.Text.addText(this.scale.width / 2 - 1000, this.scale.height - 170, '34pt', 0xffffff, FontKeys._dimboregular,ABCyaPhaser.Localization.instance.getLocalizedString('questions.set_clock_to').replace('~', timeString),this, true).setOrigin(0.5,0);

    let delay = 0;
    if (this.answersCorrect > 0) {
      delay = 500;
    }
    ABCyaPhaser.Tweens.addTween(this.timeQuestion, 1300, { delay, x: this.timeQuestion.x + 1000 }, Phaser.Math.Easing.Bounce.Out);
  }

  makeTimeQuestionLevel2() {
    this.makeTimeQuestionLevel1();
  }

  makeTimeQuestionLevel3 () {
    if (this.timeQuestion) {
      const currentTimeQuestion = this.timeQuestion;
      const finalPosition = currentTimeQuestion.x + 1000;
      ABCyaPhaser.Tweens.addTween(currentTimeQuestion, 300, { x: currentTimeQuestion.x - 150 }, Phaser.Math.Easing.Linear, () => {
        ABCyaPhaser.Tweens.addTween(currentTimeQuestion, 600, { x: finalPosition }, Phaser.Math.Easing.Linear, () => {
          currentTimeQuestion.destroy();
        });
      });
    }

    // choose a time phrase
    const timePhrases = ['half_past', 'twenty_past', 'quarter_past', 'ten_past', 'five_past', 'twenty_to', 'quarter_to', 'ten_to', 'five_to'];

    const timePhraseIndex = Phaser.Math.Between(0, timePhrases.length - 1);
    const phrase = ABCyaPhaser.Localization.instance.getLocalizedString(`questions.phrases.${timePhrases[timePhraseIndex]}`);

    this.questionTime = 'AM';
    this.questionHour = Phaser.Math.Between(1, 12);

    let hour = this.questionHour;

    switch (timePhraseIndex) {

      case 0:
        this.questionMinutes = 30;
        break;

      case 1:
        this.questionMinutes = 20;
        break;

      case 2:
        this.questionMinutes = 15;
        break;

      case 3:
        this.questionMinutes = 10;
        break;

      case 4:
        this.questionMinutes = 5;
        break;

      case 5:
        this.questionMinutes = 40;
        hour++;
        break;

      case 6:
        this.questionMinutes = 45;
        hour++;
        break;

      case 7:
        this.questionMinutes = 50;
        hour++;
        break;

      case 8:
        this.questionMinutes = 55;
        hour++;
        break;
    }

    if (hour > 12) {
      hour = 1;
    }

    const timeChoice = Phaser.Math.Between(0,1);
    if (timeChoice === 1) {
      this.questionTime = 'PM';
    }

    let timeString = `${phrase} ${hour} ${this.getLocalizedTimeLabel()}`;
    if (ABCyaLib.Environment.instance.locale === ABCyaLib.Environment.LOCALES.esUS) {
      timeString = `${hour} ${phrase} ${this.getLocalizedTimeLabel()}`;
    }

    this.timeQuestion = ABCyaPhaser.Text.addText(this.scale.width / 2 - 1000, this.scale.height - 170, '34pt', 0xffffff, FontKeys._dimboregular,ABCyaPhaser.Localization.instance.getLocalizedString('questions.set_clock_to').replace('~', timeString), this, true).setOrigin(0.5, 0);

    // special case for things like "20 to 12 AM"
    // the answer has to be OPPOSITE time (ie. 'PM")
    // so we set timeString to opposite so when we check answer all will jive
    if (hour === 12 && timePhraseIndex >= 5 && timePhraseIndex < 9 && this.questionTime === 'AM') {
      this.questionTime = 'PM';
    }
    else if (hour === 12 && timePhraseIndex >= 5 && timePhraseIndex < 9 && this.questionTime === 'PM') {
      this.questionTime = 'AM';
    }

    let delay = 0;
    if (this.answersCorrect > 0) {
      delay = 500;
    }
    ABCyaPhaser.Tweens.addTween(this.timeQuestion, 1300, { delay, x: this.timeQuestion.x + 1000 }, Phaser.Math.Easing.Bounce.Out);
  }

  makeTimeQuestionLevel4() {
    if (this.timeQuestion) {
      const currentTimeQuestion = this.timeQuestion;
      const currentTimeQuestionBg = this.timeQuestionBg;
      const finalPosition = currentTimeQuestion.x + 1000;
      ABCyaPhaser.Tweens.addTween([currentTimeQuestion, currentTimeQuestionBg], 300, { x: currentTimeQuestion.x - 150 }, Phaser.Math.Easing.Linear, () => {
        ABCyaPhaser.Tweens.addTween([currentTimeQuestion, currentTimeQuestionBg], 600, { x: finalPosition }, Phaser.Math.Easing.Linear, () => {
          currentTimeQuestion.destroy();
        });
      });
    }

    const questions = [
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.1.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.1.2'),],
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.2.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.2.2'),],
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.3.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.3.2'),],
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.4.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.4.2'),],
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.5.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.5.2'),],
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.6.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.6.2'),],
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.7.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.7.2'),],
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.8.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.8.2'),],
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.9.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.9.2'),],
      [ABCyaPhaser.Localization.instance.getLocalizedString('questions.10.1'),ABCyaPhaser.Localization.instance.getLocalizedString('questions.10.2'),]
    ];

    const minutes = Phaser.Math.Between(1, 12) * 5;

    let hour = 0;
    let minutesRemaining = minutes;
    if (minutes > 55) {
      hour = 1;
      minutesRemaining = minutes - 60;
    }

    this.questionTime = 'AM';
    this.questionHour = Phaser.Math.Between(1, 12);
    this.questionMinutes = Phaser.Math.Between(1, 11) * 5;

    const questionHourStart = this.questionHour;

    const timeChoice = Phaser.Math.Between(0,1);
    if (timeChoice === 1) {
      this.questionTime = 'PM';
    }

    const minutesString = this.questionMinutes < 10 ? `0${this.questionMinutes}` : this.questionMinutes;
    const timeString = `${this.questionHour}:${minutesString} ${this.getLocalizedTimeLabel()}`;

    // now set correct answer by adding minutes and hours to current time
    if (hour > 0) {
      this.questionHour++;
    }

    this.questionMinutes += minutesRemaining;

    if (this.questionMinutes >= 60) {
      const remainder = this.questionMinutes - 60;
      this.questionMinutes = remainder;

      this.questionHour++;
    }

    // adding minutes from starting may sometimes change time of day from AM to PM or vice versa
    if ((questionHourStart < 12 || (questionHourStart === 12 && this.questionMinutes === 0))
        && (this.questionHour > 12 || (this.questionHour === 12 && this.questionMinutes >= 0))) {
      if (this.questionTime === 'AM') {
        this.questionTime = 'PM';
      }
      else {
        this.questionTime = 'AM';
      }
    }

    if (this.questionHour > 12) {
      this.questionHour = 1;
    }

    // now substitute time and minutes into questions
    /** @type {string[]} */
    const questionArray = questions[this.answersCorrect];
    let question1 = questionArray[0];
    let question2 = questionArray[1];
    question1 = question1.replace('XX', minutes);
    question1 = question1.replace('YY', timeString);

    question2 = question2.replace('XX', minutes);
    question2 = question2.replace('YY', timeString);

    this.timeQuestionBg = this.add.graphics({ fillStyle: { color: 0x076360, alpha: 0.5 } });
    this.timeQuestion = ABCyaPhaser.Text.addText(this.scale.width / 2 - 1000, this.scale.height - 204, '22pt', 0xffffff, FontKeys._dimboregular, `${question1}\n\n${question2}`, this, true).setOrigin(0.5);
    this.timeQuestion.setLineSpacing(-5);
    this.timeQuestion.setWordWrapWidth(640);

    this.timeQuestionBg.fillRoundedRect(-this.timeQuestion.width / 2 - 25,-this.timeQuestion.height / 2 - 10,this.timeQuestion.width + 50, this.timeQuestion.height + 20, 10);
    this.timeQuestionBg.setPosition(this.timeQuestion.x , this.timeQuestion.y);

    let delay = 0;
    if (this.answersCorrect > 0) {
      delay = 500;
    }
    ABCyaPhaser.Tweens.addTween([this.timeQuestion, this.timeQuestionBg], 1300, { delay, x: this.timeQuestion.x + 1000 }, Phaser.Math.Easing.Bounce.Out);
  }

  hideAll () {
    for (let i = 0; i < this.bars.length; i++) {
      const bar = this.bars[i];
      bar.destroy();
    }
    this.clock.alpha = 0;
    this.progress.alpha = 0;
    this.musicButton.alpha = 0;
    this.infoButton.alpha = 0;
    this.checkButton.alpha = 0;
    this.menuButton.alpha = 0;
  }

  cleanUpLevel () {
    this.tweens.killAll();
    this.time.clearPendingEvents();
    this.time.removeAllEvents();

    if (ABCyaLib.Environment.instance.gameConfig.levelsOverride === null) {
      this.scene.start(SelectLevelScreen.KEY);
    }
    else if (ABCyaLib.Environment.instance.gameConfig.gameMode === null) {
      // go to the clock selection screen if needed
      this.scene.start(ClockScreen.KEY);
    }
    else {
      // go back to the intro screen
      this.scene.start(IntroScreen.KEY);
    }
  }

  checkAnswer () {
    const hourMinuteArray = this.clock.getHoursAndMinutes();

    const clockHour = hourMinuteArray[0];
    const clockMinute = hourMinuteArray[1];
    const timeOfDay = hourMinuteArray[2];

    if (clockHour === this.questionHour && clockMinute === this.questionMinutes && timeOfDay === this.questionTime) {
      ABCyaPhaser.Audio.playOneShot(Phaser.Utils.Array.GetRandom([SoundAssetKeys._correct1, SoundAssetKeys._correct2]), this);

      this.answersCorrect++;

      this.showProgress();

      if (this.answersCorrect === 10) {
        this.showLevelComplete();
        return;
      }

      this.makeQuestion();
    }
    else {
      if (this.wrong) return;
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._incorrect, this);

      this.wrong = ABCyaPhaser.Text.addText(this.scale.width / 2, 50, '36pt', 0xffffff, FontKeys._dimboregular, ABCyaPhaser.Localization.instance.getLocalizedString('game_screen.try_again'), this, true).setOrigin(0.5, 0)
        .setResolution(2);

      ABCyaPhaser.Tweens.addTween(this.wrong, 700, { scale: 2.5 }, Phaser.Math.Easing.Linear, () => {
        ABCyaPhaser.Tweens.addTween(this.wrong, 300, { scale: 1 }, Phaser.Math.Easing.Bounce.Out, () => {
          this.wrong.destroy();
          this.wrong = null;
        });
      });
    }
  }

  showProgress () {
    for (let i = this.answersCorrect - 1; i < this.answersCorrect; i++) {
      const bar = ABCyaPhaser.Images.addImage(341 + (i * 45.5), this.progress.y, `progress${i + 1}`, this);
      this.bars.push(bar);

      ABCyaPhaser.Tweens.addTween(bar, 300, { scale: 1.5 }, Phaser.Math.Easing.Linear, () => {
        ABCyaPhaser.Tweens.addTween(bar, 700, { scale: 1 }, Phaser.Math.Easing.Bounce.Out);
      });
    }
  }

  showLevelComplete () {
    const text = ABCyaPhaser.Text.addText(this.scale.width / 2, 50, '36pt', 0xffff00, FontKeys._dimboregular, ABCyaPhaser.Localization.instance.getLocalizedString('game_screen.level_complete'), this, true).setOrigin(0.5, 0)
      .setResolution(2);

    ABCyaPhaser.Tweens.addTween(text, 1000, { scale: 3 }, Phaser.Math.Easing.Linear, () => {
      this.timeQuestion.destroy();
      this.timeQuestionBg?.destroy();
      this.timeQuestion = null;
      this.timeQuestionBg = null;
      this.rocketLiftOff();
      text.destroy();
    });
  }

  rocketLiftOff () {
    this.hideAll();
    this.rocket.liftOff();

    this.time.delayedCall(4000, this.transitionOut, null, this);
  }

  transitionOut () {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._transition, this);

    const totalRotation = -2 * 360;
    this.tweens.add({ targets: { value: 0 },
      duration: 700,
      ease: Phaser.Math.Easing.Linear,
      props: { value: 1 },
      onUpdate: (tween, target) => {
        this.cameras.main.setAngle(target.value * totalRotation);
      } });
    ABCyaPhaser.Tweens.addTween(this.cameras.main,700, { zoom: 0 }, Phaser.Math.Easing.Linear, () => this.cleanUp());
  }

  cleanUp () {
    this.tweens.killAll();
    this.time.clearPendingEvents();
    this.time.removeAllEvents();

    gameData.CURRENT_LEVEL++;

    const numberOfCustomLevels = ABCyaLib.Environment.instance.gameConfig.levelsOverride?.length;
    const numberOfLevels = numberOfCustomLevels ?? 4; // default is 4 levels

    if (gameData.CURRENT_LEVEL > numberOfLevels) {

      gameData.CURRENT_LEVEL = 0;

      this.scene.start(EndScreen.KEY);
    }
    else {
      // unlock next level
      gameData.LEVELS_LOCKED[gameData.CURRENT_LEVEL] = false;

      // restart game screen
      this.scene.start();
    }
  }

  showHelpPage () {
    this.helpPage = new HelpPage(this);
    this.add.existing(this.helpPage);

    this.helpPage.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.closeHelpPage,this);
  }

  closeHelpPage () {
    this.helpPage.destroy();
  }

  getLocalizedTimeLabel() {
    return ABCyaPhaser.Localization.instance.getLocalizedString(`time_labels.${this.questionTime === 'AM' ? 'ante_meridiem' : 'post_meridiem'}`);
  }
}